import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "antd";
import {
  Card,
  Icon,
  Breadcrumb,
  Switcher,
  Tooltip,
  AccordionItemV2,
  Font,
  Tab,
  TabsNavigation,
  LoadingSpinner,
} from "pepsico-ds";
import { FetchLandingPageAction, FetchUserInfoAction } from "../../redux/action/LandingPageAction";
import { FetchMIL2Action } from "../../redux/action/CommodityPackagingAction";
import { getUserClaims } from "../../utils/auth/unified-auth";
import { useAuth } from "../../utils/auth/useAuth";
import { call7DaysNpsLogic, returnHyphenIfNull } from '../../utils/utils';
import {
  FetchSBACCIDateAction,
  FetchSBACCIDetailPageAction,
  FetchWoodmacReportDateAction,
  FetchWoodmacGetReportPageAction,
  // FetchWoodmacDetailReportAction
} from "../../redux/action/SBACCIAction";
import {
  changeFromDateOptionsUtil,
  changeToDateOptionsUtil,
} from "../KPIOverview/KpiUtils";
import SingleSelectDropdown from "../../components/Dropdown/SingleSelectDropdown";
import { localStoreKeys } from "../../constants/localStoreKeys";
import ActualLegendRISI from "../../assets/ActualLegendRISI.svg";
import DropdownWithIcons from "../../components/Modal/DropdownWithIcons";
import { useToast } from '../../components/Toast/ToastContext';
import Vector from '../../assets/Vector.png'
import { setKeyValue } from "../../redux/reducer/localDataSlice";
import CustomButtons from "../../components/Cards/CustomButtons";
import CmaSource from "./sources/CmaSource";
import WoodmacSource from "./sources/WoodmacSource";
import SbacciSource from "./sources/SbacciSource";
import SPSource from "./sources/SPSource";
import SmartCubeSource from "./sources/SmartCubeSource";
import RisiSource from "./sources/RisiSource";
import { CmaSourceProvider, useCmaSource } from "./sources/CmaSourceContext";
import { WoodmacSourceProvider, useWoodmacSource } from "./sources/WoodmacSourceContext";
import { SPGlobalSourceProvider, useSPGlobalSource } from "./sources/SPGlobalSourceContext";
import { SmartCubeSourceProvider, useSmartCubeSource } from "./sources/SmartCubeSourceContext";
import { RisiSourceProvider, useRisiSource } from "./sources/RisiSourceContext";

// Shared "whole page download / app list" controls rendered in the header for
// all three sources (CMA, Woodmac, SBACCI). For CMA/Woodmac this needs LIVE
// unit/table data that now lives in CmaSourceContext/WoodmacSourceContext, so
// this component is rendered as a child of both providers and consumes them
// directly via hooks (the parent SBACCIScreen itself cannot, since it's the
// one creating those providers).
/* istanbul ignore next */
const PricingPageDownloadControls = ({ isCmaSelected, isWoodmacSelected, isSBACCISelected, isSPGlobalSelected, isSmartCubeSelected, isRisiSelected, selectedTab, cmaFullPageRef }) => {
  const cma = useCmaSource();
  const woodmac = useWoodmacSource();
  const spGlobal = useSPGlobalSource();
  const smartCube = useSmartCubeSource();
  const risi = useRisiSource();
  const { showToast } = useToast();
  const activeAppList = isWoodmacSelected
    ? woodmac?.activePricingTableData?.apps
    : isCmaSelected
      ? cma?.activePricingTableData?.apps
      : isSPGlobalSelected
        ? spGlobal?.filterData?.apps
        : isSmartCubeSelected
          ? smartCube?.smartCubeFilterData?.apps
          : isRisiSelected
            ? risi?.risiData?.apps
            : undefined;

  /* istanbul ignore next */
  const sbacciCheckPDFDownloaded = (isPdfDownloaded, isModal, fileURL, isExcel, isTable, isJpeg) => {
    if (isPdfDownloaded) {
      if (isModal) {
        showToast("success", !isJpeg ? "PDF of full page successfully downloaded." : "JPEG of full page successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else if (isTable) {
        showToast("success", !isJpeg ? "PDF of a table successfully downloaded." : "JPEG of a table successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else {
        showToast("success", !isJpeg ? "PDF of a chart successfully downloaded." : "JPEG of a chart successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      }
    } else if (!isExcel) {
      showToast("Error", "Failed to download. Please try again.", {
        icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
      });
    }
  };

  const activeCheckPDFDownloaded = isWoodmacSelected
    ? woodmac?.checkPDFDownloaded
    : isCmaSelected
      ? cma?.checkPDFDownloaded
      : isSPGlobalSelected
        ? spGlobal?.checkPDFDownloaded
        : isSmartCubeSelected
          ? smartCube?.checkPDFDownloaded
          : isRisiSelected
            ? risi?.checkPDFDownloaded
            : sbacciCheckPDFDownloaded;

  return (
    <span style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      {(isCmaSelected || isWoodmacSelected || isSPGlobalSelected) && (
        <Tooltip direction="left" text={"This application is not SSO enabled"}>
          <Icon alt="" icon="info" size="small" style={{ marginRight: "10px" }} />
        </Tooltip>
      )}
      <CustomButtons isModal={selectedTab === 1} appList={activeAppList} />
      {selectedTab === 1 && (
        <span style={{ float: "right", position: "relative", right: "1rem", bottom: "0.15rem" }}>
          <DropdownWithIcons
            modalRef={cmaFullPageRef}
            isModal={true}
            kpiName={isWoodmacSelected ? "Woodmac" : isSBACCISelected ? "SBACCI" : isSPGlobalSelected ? "S&P Global" : isSmartCubeSelected ? "SmartCube" : isRisiSelected ? "RISI_Modal" : "CMA"}
            checkPDFDownloaded={activeCheckPDFDownloaded}
          />
        </span>
      )}
    </span>
  );
};

const SBACCIScreen = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { category, subcategory } = useParams();
  const sections = [
        { key: "executiveSummaryReport", label: "Executive Summary" },
        { key: "rPETMarketInsightReport", label: "Market Insights" },
        { key: "rPETMonthlyEconomicReport", label: "Economic Insights" },
        { key: "petMonthlyReport", label: "Monthly Review" },
        { key: "petMarginReport", label: "Price Margin Forecast" },
    ];

  const woodmacReportSections = [
        { key: "petFlexiblePackagingReport", label: "Flexible Packaging Overview" },
        { key: "petMarketOverviewReport", label: "PET Market Overview" },
        { key: "rPETInvestmentOutlookReport", label: "rPET Investment Outlook" },
        { key: "rPETMarketOverviewReport", label: "rPET Market Overview" },
    ];
  const [minYear, setMinYear] = useState(2023);
  const [maxYear, setMaxYear] = useState(2024);
  const [datePickerMenu, setDatePickerMenu] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const [isUserDataFetched, setIsUserDataFetched] = useState(false);
  const [isSBACCIDataLoading, setIsSBACCIDataLoading] = useState(false);
  const [isWoodmacDataLoading, setIsWoodmacDataLoading] = useState(false);
  const [isCMADataLoading, setIsCMADataLoading] = useState(false);
  const [dateRange, setDateRange] = useState({});
  const [pdfReportData, setPdfReportData] = useState({});
  const [cmaPdfReportData, setCmaPdfReportData] = useState({});
  const [switcherIndex, setSwitcherIndex] = useState(0);
  const [woodmacPricingSwitcherIndex, setWoodmacPricingSwitcherIndex] = useState(0);
  const [woodmacReportsSwitcherIndex, setWoodmacReportsSwitcherIndex] = useState(0);
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [fromDates, setFromDates] = useState([]);  
  const [toDates, setToDates] = useState([]);

  const [entireMonthList, setEntireMonthList] = useState([]);
  const [fromDateSelected, setFromDateSelected] = useState("");
  const [toDateSelected, setToDateSelected] = useState("");
  const [sourceSelected, setSourceSelected] = useState("");
  const [selectedTab, setSelectedTab] = useState(1);
  const [isCmaSelected, setIsCmaSelected] = useState(true);
  const [isWoodmacSelected, setIsWoodmacSelected] = useState(false);
  const [isSBACCISelected, setIsSBACCISelected] = useState(false);
  const [isSPGlobalSelected, setIsSPGlobalSelected] = useState(false);
  const [isSmartCubeSelected, setIsSmartCubeSelected] = useState(false);
  const [isRisiSelected, setIsRisiSelected] = useState(false);
  const [woodmacValueTypeSelected, setwoodmacValueTypeSelected] = useState([]);
  const [selectedAccordionKey, setSelectedAccordionKey] = useState(null);
  const [selectedCmaAccordionKey, setSelectedCmaAccordionKey] = useState(null);
  const [selectedWoodmacAccordionKey, setSelectedWoodmacAccordionKey] = useState(null);
  const feedbackFormUrl = useSelector(

    state => state?.localData?.data[localStoreKeys.feedbackFormUrl]
  );
  const linechartRef = useRef();
  const cmaTableRef = useRef();
  const cmaFullPageRef = useRef();
  const userData = useSelector(state => state.getUserInfoReducer);

  useEffect(() => {
    async function fetchUserInfo() {
      const user = await getUserClaims();
      if (user) {
        setUserInfo(user);
        dispatch(FetchUserInfoAction(user));
        dispatch(FetchLandingPageAction(user));
        setIsUserDataFetched(true);
      }
    }
    setIsCMADataLoading(true);
    fetchUserInfo();
    dispatch(FetchMIL2Action(category, userData?.emailId));
  }, []);
  
  const miL2Data = useSelector(state => state.getMIL2Reducer);
  const dateData = useSelector(state => state.getSBACCIDateReducer);
  const reportData = useSelector(state => state.getSBACCIDetailReducer);
  const cmaFilterData = useSelector(state => state.getCMAPageFilterReducer);
  const cmaLineChartData = useSelector(state => state.getCMALineChartReducer);
  const cmaTableData = useSelector(state => state.getCMATableReducer);
  const cmaDateData = useSelector(state => state.getCMADateReducer);
  const cmaDetailReportData = useSelector(state => state.getCmaDetailReportReducer);
  const woodmacFilterData = useSelector(state => state.getWoodmacPageFilterReducer);
  const woodmacLineChartData = useSelector(state => state.getWoodmacLineChartReducer);
  const woodmacTableData = useSelector(state => state.getWoodmacTableReducer);
  const woodmacDateData = useSelector(state => state.getWoodmacDateReducer);
  const woodmacDetailReportData = useSelector(state => state.getWoodmacDetailReportReducer);
  const spGlobalLabelCountData = useSelector(state => state.getSPGlobalPageLabelCountReducer);
  const spGlobalFilterData = useSelector(state => state.getSPGlobalPageFilterReducer);
  const spGlobalDetailReportData = useSelector(state => state.getSPGlobalDetailReportReducer);
  const smartCubeFilterData = useSelector(state => state.getSmartCubePageFilterReducer);
  // const risiDetailData = useSelector(state => state.getRISIDetailReducer);
  const risiDetailData = useSelector(state => state.getRISIDetailV2Reducer);
  // const cmaPdfReportData = useSelector(state => state.getCmaPdfReportReducer);
  
  const activePricingFilterData = isWoodmacSelected ? woodmacFilterData : cmaFilterData;
  const activePricingLineChartData = isWoodmacSelected ? woodmacLineChartData : cmaLineChartData;
  const activePricingTableData = isWoodmacSelected ? woodmacTableData : isCmaSelected ? cmaTableData : null;
  const activePricingDateData = isWoodmacSelected ? woodmacDateData : cmaDateData;
  const activePricingDetailReportData = isWoodmacSelected ? woodmacDetailReportData : cmaDetailReportData;

  // const cmaReportCount = cmaLineChartData?.reportCount? cmaLineChartData?.reportCount:0;
  const cmaReportCount = cmaFilterData?.cmaReportCount;
  const cmaLastUpdate = selectedTab == 1 ? activePricingTableData?.dateAsOn : activePricingDetailReportData?.lastUpdate;
  const cmaRefreshFrequency = selectedTab == 1 ? activePricingTableData?.frequency : activePricingDetailReportData?.refreshFrequency;
  const cmaAppOwners = activePricingTableData?.appOwners;
  // const sbacciReportCount = reportData?.reportCount? reportData?.reportCount:0;
  const sbacciReportCount = cmaFilterData?.sbaCCICount;
  const woodmacReportCount = cmaFilterData?.woodMacReportCount;
  const sbacciLastUpdate = reportData?.lastUpdate;
  const sbacciRefreshFrequency = reportData?.refreshFrequency;
  const sbacciAppOwners = reportData?.appOwner;
  const spGlobalReportCount = spGlobalFilterData?.reportCount;
  const spGlobalLastUpdate = selectedTab == 1 ? spGlobalFilterData?.dateAsOn : spGlobalDetailReportData?.lastUpdate;
  const spGlobalRefreshFrequency = selectedTab == 1 ? spGlobalFilterData?.refreshFrequency : spGlobalDetailReportData?.refreshFrequency;
  const spGlobalAppOwners = spGlobalFilterData?.appOwners;
  const smartCubeAppOwners = smartCubeFilterData?.appOwners;
  const smartCubeDateAsOn = smartCubeFilterData?.dateAsOn;
  const smartCubeRefreshFrequency = smartCubeFilterData?.refreshFrequency;
  const risiAppOwners = risiDetailData?.owners;
  const risiLastUpdate = risiDetailData?.risiData?.lastUpdate;
  const risiRefreshFrequency = risiDetailData?.risiData?.refreshFrequency;

  // TO BE DELETED
  // useEffect(() => {
  //   const fetchFilterData = async () => {
  //     await dispatch(FetchCMAPageFilterAction());
  //   }
  //   fetchFilterData();
  // }, [])
  // useEffect(() => {
  //   const fetchCmaDateData = async () => {
  //     await dispatch(FetchCMADateAction());
  //   }
  //   fetchCmaDateData();
  // },[])

const cmaReportSections = [
    { key: "weeklyReport", label: "Weekly" },
    { key: "monthlyReport", label: "Monthly" }
  ];

    const activeTabSectionKeys = switcherIndex === 0
        ? ["executiveSummaryReport", "petMonthlyReport", "petMarginReport"]
        : ["executiveSummaryReport", "rPETMarketInsightReport", "rPETMonthlyEconomicReport"];

    const woodmacActiveTabSectionKeys = woodmacReportsSwitcherIndex === 0
        ? ["petFlexiblePackagingReport", "petMarketOverviewReport"]
        : ["rPETInvestmentOutlookReport", "rPETMarketOverviewReport"];

    const getVisibleSections = (data) => sections.filter((section) => Array.isArray(data?.[section.key]) && data[section.key].length > 0);
    const renderedSections = getVisibleSections(reportData).filter((section) => activeTabSectionKeys.includes(section.key));
    const accordionSelectedIndex = renderedSections.findIndex((section) => section.key === selectedAccordionKey);
    const renderedWoodmacSections = woodmacReportSections
      .filter((section) => Array.isArray(cmaPdfReportData?.[section.key]) && cmaPdfReportData[section.key].length > 0)
      .filter((section) => woodmacActiveTabSectionKeys.includes(section.key));
    const woodmacAccordionSelectedIndex = renderedWoodmacSections.findIndex(
      section => section.key === selectedWoodmacAccordionKey
    );

   
    const renderedCmaSections = cmaReportSections.filter(
      section => Array.isArray(cmaPdfReportData?.[section.key]) && cmaPdfReportData[section.key].length > 0
    );
    const cmaAccordionSelectedIndex = renderedCmaSections.findIndex(
      section => section.key === selectedCmaAccordionKey
    );
    /* istanbul ignore next */
    const cellDataFunction = (ctx) => {
        const colId = ctx.column.id
        if(ctx.row.original[colId]==null) {
            return <div>-</div>
        }
        return <div>{ctx.row.original[colId]}</div>;
    }

  // useEffect(() => {
  //   if (
  //     isUserDataFetched &&
  //     userData &&
  //     userData.type === "SUCCESS_LANDING_PAGE_ACTION"
  //   ) {
  //     call7DaysNpsLogic(dispatch, userData);

  //     setIsDataLoading(true);

  //     dispatch(FetchSBACCIDateAction());
  //   }
  // }, [userData]);

  // Select/open report based on active tab and defaultId (if present)
    useEffect(() => {
        if (!reportData) return;

        const tabSectionKeys = activeTabSectionKeys;

        const defaultId = reportData?.defaultId;
        let selectedKey = null;
        let selectedUrl = null;

        // 1) Prefer defaultId match, but only within the active tab sections.
        if (defaultId) {
            for (const key of tabSectionKeys) {
                const items = reportData?.[key];
                if (Array.isArray(items)) {
                    const matchedReport = items.find((report) => report.reportId === defaultId);
                    if (matchedReport) {
                        selectedKey = key;
                        selectedUrl = matchedReport.url;
                        break;
                    }
                }
            }
        }

        // 2) Fallback to the first available section in the active tab.
        if (!selectedKey) {
            for (const key of tabSectionKeys) {
                const items = reportData?.[key];
                if (Array.isArray(items) && items.length > 0) {
                    selectedKey = key;
                    selectedUrl = items?.[0]?.url || null;
                    break;
                }
            }
        }

        setSelectedAccordionKey(selectedKey);
        setPdfUrl(selectedUrl);
    }, [reportData, switcherIndex]);

    /* istanbul ignore next */
    useEffect(() => {
      if (!isWoodmacSelected || selectedTab !== 2) return;

      const woodmacSectionKeys = renderedWoodmacSections.map(section => section.key);
      const defaultId = cmaPdfReportData?.defaultId;
      let selectedKey = null;
      let selectedUrl = null;

      if (defaultId) {
        for (const key of woodmacSectionKeys) {
          const items = cmaPdfReportData?.[key];
          if (Array.isArray(items)) {
            const matchedReport = items.find(report => report.reportId === defaultId);
            if (matchedReport) {
              selectedKey = key;
              selectedUrl = matchedReport.url;
              break;
            }
          }
        }
      }

      if (!selectedKey) {
        for (const key of woodmacSectionKeys) {
          const items = cmaPdfReportData?.[key];
          if (Array.isArray(items) && items.length > 0) {
            selectedKey = key;
            selectedUrl = items?.[0]?.url || null;
            break;
          }
        }
      }

      setSelectedWoodmacAccordionKey(selectedKey);
      setPdfUrl(selectedUrl);
    }, [isWoodmacSelected, selectedTab, cmaPdfReportData, woodmacReportsSwitcherIndex]);
    
    /* istanbul ignore next */
    useEffect(() => {
      if (!isCmaSelected || selectedTab !== 2) return;

      const cmaSectionKeys = renderedCmaSections.map(section => section.key);
      const defaultId = cmaPdfReportData?.defaultId;
      let selectedKey = null;
      let selectedUrl = null;

      // 1) Prefer defaultId match, but only within available CMA sections.
      if (defaultId) {
        for (const key of cmaSectionKeys) {
          const items = cmaPdfReportData?.[key];
          if (Array.isArray(items)) {
            const matchedReport = items.find(report => report.reportId === defaultId);
            if (matchedReport) {
              selectedKey = key;
              selectedUrl = matchedReport.url;
              break;
            }
          }
        }
      }

      // 2) Fallback to the first available CMA section/report.
      if (!selectedKey) {
        for (const key of cmaSectionKeys) {
          const items = cmaPdfReportData?.[key];
          if (Array.isArray(items) && items.length > 0) {
            selectedKey = key;
            selectedUrl = items?.[0]?.url || null;
            break;
          }
        }
      }

      setSelectedCmaAccordionKey(selectedKey);
      setPdfUrl(selectedUrl);
    // }, [isCmaSelected, selectedTab, cmaPdfReportData, renderedCmaSections]);
    }, [isCmaSelected, selectedTab, cmaPdfReportData]);

    useEffect(() => {
    if (
      isUserDataFetched &&
      userData &&
      userData.type === "SUCCESS_USER_INFO_ACTION"
    ) {
      if (userData?.feedbackFormUrl) {
          dispatch(setKeyValue({ key: localStoreKeys.feedbackFormUrl, value: userData.feedbackFormUrl }));
      }
      call7DaysNpsLogic(dispatch, userData);
    }
  }, [userData]);
    // useEffect(() => {
    //     if (isUserDataFetched && 
    //       reportData && 
    //       reportData.type === "SUCCESS_SBA_CCI_DETAIL_ACTION") {

    //         // console.log("reportData", reportData)
    //         setIsSBACCIDataLoading(false);
    //     }
		
    // }, [reportData])
    /* istanbul ignore next */
    // const formatMonthData = (dateStr) => {
    //     if (!dateStr || dateStr.length < 6) return null;
    // }
  useEffect(() => {
    if (
      isUserDataFetched &&
      reportData &&
      reportData.type === "SUCCESS_SBA_CCI_DETAIL_ACTION"
    ) {
      // setPdfReportData(reportData);
      setIsSBACCIDataLoading(false);
    }
  }, [reportData]);

  useEffect(() => {
    if (
      isUserDataFetched &&
      woodmacDetailReportData &&
      woodmacDetailReportData.type === "SUCCESS_WOODMAC_DETAIL_REPORT_ACTION"
    ) {
      // setPdfReportData(reportData);
      setIsWoodmacDataLoading(false);
    }
  }, [woodmacDetailReportData]);
  //cmaDetailReportData
   useEffect(() => {
    if (
      isUserDataFetched &&
      activePricingDetailReportData &&
      activePricingDetailReportData.type === "SUCCESS_WOODMAC_DETAIL_REPORT_ACTION"
    ) {
      setCmaPdfReportData(activePricingDetailReportData);
    }
  }, [activePricingDetailReportData]);
  /* istanbul ignore next */
  const formatMonthData = dateStr => {
    if (!dateStr || dateStr.length < 6) return null;

    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);

    const dateObj = new Date(`${year}-${month}-01`);

    const displayText = dateObj.toLocaleString("en-US", {
      month: "short",
      year: "numeric"
    });

    return {
      displayText, // "Mar 2025"
      childContents: [],
      id: `${year}-${parseInt(month, 10)}`, // "2025-3"
      value: `${year}-${month}-01` // "2025-03-01"
    };
  };

  useEffect(() => {

    let dateVal = isWoodmacSelected ? woodmacDateData : dateData;

    if (
      isUserDataFetched &&
      dateVal &&
      (dateVal.type === "SUCCESS_SBA_CCI_DATE_ACTION" || 
      dateVal.type === "SUCCESS_WOODMAC_DATE_ACTION")
    ) {
      const minDate = new Date(dateVal?.minDate);
      setMaxYear(new Date(dateVal?.maxDate)?.getUTCFullYear());
      minDate.setDate(minDate.getDate() - 1);
      setMinYear(minDate?.getUTCFullYear());

      setDatePickerMenu({
        disabled: {
          afterDate: new Date(dateVal?.maxDate),
          weekends: false,
          beforeDate: minDate
        }
      });
      const dateRangeSelected = {
        to: new Date(dateVal?.maxDate),
        from: new Date(dateVal?.fromDate)
      };
      setDateRange(dateRangeSelected);

      const months = generateMonthObjects(
        dateVal?.fromDate,
        dateVal?.maxDate
      );

      setEntireMonthList(months);

      setFromDates(months);
      setToDates(months);
      let fromDateVal = [formatMonthData(dateVal?.fromDate)];
      let toDateVal = [formatMonthData(dateVal?.maxDate)];
      setFromDateSelected(fromDateVal);
      setToDateSelected(toDateVal);

      if(isWoodmacSelected) {
        localStorage.setItem(
        "woodmac-fromDateSelected",
        JSON.stringify(fromDateVal)
        );
        localStorage.setItem("woodmac-toDateSelected", JSON.stringify(toDateVal));

        localStorage.setItem("ph_woodmac_switch", 0);

      } else if (isSBACCISelected) {
        localStorage.setItem(
        "sbacci-fromDateSelected",
        JSON.stringify(fromDateVal)
        );
        localStorage.setItem("sbacci-toDateSelected", JSON.stringify(toDateVal));

        localStorage.setItem("ph_sba_cci_switch", 0);
      }
      

      localStorage.setItem(
        "dateRangeSelected_sba",
        JSON.stringify(dateRangeSelected)
      );

      
      
      
      loadKPIPageData();
    }
  }, [dateData, woodmacDateData]);

useEffect(() => {
  if(selectedTab == 2 && isWoodmacSelected) {
    setWoodmacReportsSwitcherIndex(0)
    setIsWoodmacDataLoading(true);
    dispatch(FetchWoodmacReportDateAction());
  }

},[selectedTab, isWoodmacSelected])

  useEffect(() => {
    return () => {
      dispatch({
        type: "RESET_SBA_CCI_DATE_ACTION",
        payload: "RESET_SBA_CCI_DATE_ACTION"
      });
      dispatch({
        type: "RESET_SBA_CCI_DETAIL_ACTION",
        payload: "RESET_SBA_CCI_DETAIL_ACTION"
      });
      dispatch({
        type: "RESET_CMA_PAGE_FILTER_ACTION",
        payload: "RESET_CMA_PAGE_FILTER_ACTION"
      });
      dispatch({
        type: "RESET_CMA_LINE_CHART_ACTION",
        payload: "RESET_CMA_LINE_CHART_ACTION"
      });
      dispatch({
        type: "RESET_CMA_DATE_ACTION",
        payload: "RESET_CMA_DATE_ACTION"});

        dispatch({
        type: "RESET_WOODMAC_PAGE_FILTER_ACTION",
        payload: "RESET_WOODMAC_PAGE_FILTER_ACTION"
      });
      dispatch({
        type: "RESET_WOODMAC_LINE_CHART_ACTION",
        payload: "RESET_WOODMAC_LINE_CHART_ACTION"
      });
      dispatch({
        type: "RESET_WOODMAC_DATE_ACTION",
        payload: "RESET_WOODMAC_DATE_ACTION"});
      dispatch({
        type: "RESET_WOODMAC_DETAIL_REPORT_ACTION",
        payload: "RESET_WOODMAC_DETAIL_REPORT_ACTION"});
      dispatch({
        type: "RESET_SPGLOBAL_PAGE_LABEL_COUNT_ACTION",
        payload: "RESET_SPGLOBAL_PAGE_LABEL_COUNT_ACTION"});
    };
  }, []);
  /* istanbul ignore next */
  useEffect(() => {
    setPdfUrl(null);
  }, [switcherIndex]);

  /* istanbul ignore next */
  const getUTCDate = d => {
    return new Date(
      Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
    )?.toISOString();
  };

  /* istanbul ignore next */
  const switchTabs = val => {
    if(isWoodmacSelected) {
      setWoodmacReportsSwitcherIndex(val);
      localStorage.setItem("ph_woodmac_switch", val);
    } else if(isSBACCISelected) {
      setSwitcherIndex(val);
      localStorage.setItem("ph_sba_cci_switch", val);
    }
    
    loadKPIPageData();
  };

  /* istanbul ignore next */
  const onDateChange = val => {
    const from = new Date(val?.from);
    const to = new Date(val?.to);
    const fromStr = val?.from ? getUTCDate(from) : null;
    const toStr = val?.to ? getUTCDate(to) : null;
    const dateData = { from: fromStr, to: toStr };

    setDateRange(val);
    localStorage.setItem("dateRangeSelected_sba", JSON.stringify(dateData));
    loadKPIPageData();
  };

  /* istanbul ignore next */
  const loadKPIPageData = () => {
    const dateRangeData = JSON.parse(
      localStorage.getItem("dateRangeSelected_sba")
    );
    const fromDate = dateRangeData?.from || null;
    const toDate = dateRangeData?.to || null;

    if(isSBACCISelected) {
      const sbaccifromDateSelected = JSON.parse(
        localStorage.getItem("sbacci-fromDateSelected")
      );
      const sbacciToDateSelected = JSON.parse(
        localStorage.getItem("sbacci-toDateSelected")
      );

      const requestObject = {
        fromDate:
          sbaccifromDateSelected?.length > 0
            ? sbaccifromDateSelected[0]?.value
            : `${dateData.fromDate?.slice(0, 4)}-${dateData.fromDate?.slice(4, 6)}-01`,
        toDate:
          sbacciToDateSelected?.length > 0
            ? sbacciToDateSelected[0]?.value
            : `${dateData.maxDate?.slice(0, 4)}-${dateData.maxDate?.slice(4, 6)}-01`,
        currentTab:
          Number(localStorage.getItem("ph_sba_cci_switch")) === 0 ? "PET" : "rPET"
      };

      setIsSBACCIDataLoading(true);
      dispatch(FetchSBACCIDetailPageAction(requestObject));
    } else if(isWoodmacSelected) {
      const woodmacFromDateSelected = JSON.parse(
        localStorage.getItem("woodmac-fromDateSelected")
      );
      const woodmacToDateSelected = JSON.parse(
        localStorage.getItem("woodmac-toDateSelected")
      );

      const requestObject = {
        fromDate:
          woodmacFromDateSelected?.length > 0
            ? woodmacFromDateSelected[0]?.value
            : `${woodmacDateData.fromDate?.slice(0, 4)}-${woodmacDateData.fromDate?.slice(4, 6)}-01`,
        toDate:
          woodmacToDateSelected?.length > 0
            ? woodmacToDateSelected[0]?.value
            : `${woodmacDateData.maxDate?.slice(0, 4)}-${woodmacDateData.maxDate?.slice(4, 6)}-01`,
        currentTab:
          Number(localStorage.getItem("ph_woodmac_switch")) === 0 ? "PET" : "rPET"
      };

      setIsWoodmacDataLoading(true);
      dispatch(FetchWoodmacGetReportPageAction(requestObject));
    }
  };

  /* istanbul ignore next */
  // const sections = [
  //   { key: "executiveSummaryReport", label: "Executive Summary" },

  //   { key: "rPETMarketInsightReport", label: "Market Insights" },
  //   { key: "rPETMonthlyEconomicReport", label: "Economic Insights" },

  //   { key: "petMonthlyReport", label: "Monthly Review" },
  //   { key: "petMarginReport", label: "Price Margin Forecast" }
  // ];
  
  /* istanbul ignore next */
  const makeLabel = (text, count) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontWeight: "bold" }}>{text}</span>
      <span
        style={{
          color: "#0662C4"
        }}
      >
        ({count})
      </span>
    </span>
  );

  /* istanbul ignore next */
  const renderSection = (items, label) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
      <AccordionItemV2
        key={label}
        // label={`${label} (${items.length})`}
        label={makeLabel(label, items.length)}
        onUpdate={function wRe() {}}
        presentation="box-shadow"
        className="custom-accordion-items"
      >
        {items.map((item, idx) => (
          <div
            key={`${label}-${idx}`}
            // style={{ marginBottom: 8 }}
          >
            <Font
              textColorClass="$color-text-secondary"
              onClick={() => {
                setPdfUrl(item.url)
              }}
              style={{
                color: item.url === pdfUrl ? "#004895" : "#2D2D2D",
                fontWeight: "bold",
                cursor: "pointer"
              }}
              aria-label="sbacci-pdf-link"
              data-fs="sbacci-pdf-link"
            >
              {item.reportName}
            </Font>
            {/* {idx < items.length - 1 && <hr style={{color: 'blue'}}/>} */}
            {idx < items.length - 1 && (
              <hr
                style={{
                  border: 0,
                  borderTop: "1px solid #D8E7FB",
                  margin: "4px 0"
                }}
              />
            )}
          </div>
        ))}
      </AccordionItemV2>
    );
  };

  /* istanbul ignore next */
  const renderForecastLegend = () => (
    <div>
      <img
        src={ActualLegendRISI}
        alt="img"
        style={{ marginRight: "1rem" }}
      />
      <span
        style={{
          marginRight: "0.25rem",
          position: "relative",
          top: "3px"
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <line
            x1="0"
            y1="6"
            x2="12"
            y2="6"
            stroke={"#616161"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="5 0"
          />
        </svg>
      </span>
      <span
        style={{
          marginRight: "0.25rem",
          position: "relative",
          top: "3px"
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <line
            x1="0"
            y1="6"
            x2="18"
            y2="6"
            stroke={"#616161"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray="5 0"
          />
        </svg>
      </span>
      <span
        style={{
          color: "#616161",
          position: "relative",
          top: "2px"
        }}
      >
        Forecast
      </span>
    </div>
  );

  /* istanbul ignore next */
  const onFromDateChange = val => {
    if (val && val.length > 0) {
      setFromDateSelected(val);
      localStorage.setItem("sbacci-fromDateSelected", JSON.stringify(val));
      // dispatch(setKeyValue({ key: localStoreKeys.fromDateDisplayValue, value: val[0]['displayText'] }));
      // const toDateOptions = getToDateOptions(val[0]['displayText']); // TO BE CHANGED
      // setToDates(toDateOptions); OLD LOGIC

      // let capabilitySearchKey = getCapabilitySearchKey(appName,capability);
      // const maxDateObject = maxDateForApps?.find(item => item.appName === capabilitySearchKey);
      // const toDateOptions1 = getToDateOptionsWithMaxDate(val[0]['displayText'], maxDateObject);
      // setToDates(toDateOptions1);

      let tempY = val[0]["displayText"]?.split(" ")?.[1];
      const date1 = new Date(val[0]["displayText"]);
      const date2 = new Date(toDateSelected?.[0]?.["displayText"]);
      const date2Year = date2?.getFullYear();
      // const toDateMonth = months[date2?.getMonth()];;

      // const tempToDate = toDateOptions1?.filter((item) => { // CHANGED
      //     return item.displayText === ('Dec ' + tempY);
      // })

      // const customTempToDate = toDateOptions1?.filter((item) => { // CHANGED
      //     return item.displayText === (`${toDateMonth} ` + date2Year);
      // })
      changeToDateOptions(val[0]);

      // if (!isNaN(date1.getTime()) && !isNaN(date2?.getTime())) {
      //     if (date1 <= date2) {
      //         setToDateSelected(customTempToDate);
      //         localStorage.setItem('toDateSelected', JSON.stringify(customTempToDate))
      //         // spendCardApiCallonChange(roleSelected, sectorSelected, businessUnitSelected, categoryL0Selected, categoryL1Selected, categoryL2Selected, val, customTempToDate, userAccessibleSectorList, 14)
      //     } else {
      //         setToDateSelected(tempToDate);
      //         localStorage.setItem('toDateSelected', JSON.stringify(tempToDate))
      //         // spendCardApiCallonChange(roleSelected, sectorSelected, businessUnitSelected, categoryL0Selected, categoryL1Selected, categoryL2Selected, val, tempToDate, userAccessibleSectorList, 15)
      //     }
      // } else {
      //     // spendCardApiCallonChange(roleSelected, sectorSelected, businessUnitSelected, categoryL0Selected, categoryL1Selected, categoryL2Selected, val, tempToDate, userAccessibleSectorList, 16)
      // }

      const requestObject = {
        // fromDate: fromDate?.slice(0, 10) || '',
        // toDate: toDate?.slice(0, 10) || '',
        fromDate: val[0]?.value,
        toDate:
          toDateSelected?.length > 0
            ? toDateSelected[0]?.value
            : `${dateData?.maxDate?.slice(0, 4)}-${dateData.maxDate?.slice(4, 6)}-01`,
        currentTab:
          Number(localStorage.getItem("ph_sba_cci_switch")) === 0
            ? "PET"
            : "rPET"
      };

      setIsSBACCIDataLoading(true);
      dispatch(FetchSBACCIDetailPageAction(requestObject));
      setPdfUrl(null);
    } 
  };
  /* istanbul ignore next */
  const onWoodmacFromDateChange = val => {
    if (val && val.length > 0) {
      setFromDateSelected(val);
      localStorage.setItem("woodmac-fromDateSelected", JSON.stringify(val));

      let tempY = val[0]["displayText"]?.split(" ")?.[1];
      const date1 = new Date(val[0]["displayText"]);
      const date2 = new Date(toDateSelected?.[0]?.["displayText"]);
      const date2Year = date2?.getFullYear();
      
      changeToDateOptions(val[0]);

      const requestObject = {
        fromDate: val[0]?.value,
        toDate:
          toDateSelected?.length > 0
            ? toDateSelected[0]?.value
            : `${woodmacDateData?.maxDate?.slice(0, 4)}-${woodmacDateData.maxDate?.slice(4, 6)}-01`,
        currentTab:
          Number(localStorage.getItem("ph_woodmac_switch")) === 0
            ? "PET"
            : "rPET"
      };

      setIsWoodmacDataLoading(true);
      dispatch(FetchWoodmacGetReportPageAction(requestObject));
      setPdfUrl(null);
    } 
  };

  /* istanbul ignore next */
  const onToDateChange = val => {
    // console.log("val");
    if (val && val.length > 0) {
      setToDateSelected(val);
      localStorage.setItem("sbacci-toDateSelected", JSON.stringify(val));
      changeFromDateOptions(val[0]);
      ////
      // spendCardApiCallonChange(roleSelected, sectorSelected, businessUnitSelected, categoryL0Selected, categoryL1Selected, categoryL2Selected, fromDateSelected, val, userAccessibleSectorList, 17)
      const requestObject = {
        // fromDate: fromDate?.slice(0, 10) || '',
        // toDate: toDate?.slice(0, 10) || '',
        fromDate:
          fromDateSelected?.length > 0
            ? fromDateSelected[0]?.value
            : `${dateData.fromDate.slice(0, 4)}-${dateData.fromDate.slice(4, 6)}-01`,
        toDate: val[0]?.value,
        currentTab:
          Number(localStorage.getItem("ph_sba_cci_switch")) === 0
            ? "PET"
            : "rPET"
      };

      setIsSBACCIDataLoading(true);
      dispatch(FetchSBACCIDetailPageAction(requestObject));
      setPdfUrl(null);
    } 
  };

  /* istanbul ignore next */
  const onWoodmacToDateChange = val => {
    if (val && val.length > 0) {
      setToDateSelected(val);
      localStorage.setItem("woodmac-toDateSelected", JSON.stringify(val));
      changeFromDateOptions(val[0]);
      ////
      // spendCardApiCallonChange(roleSelected, sectorSelected, businessUnitSelected, categoryL0Selected, categoryL1Selected, categoryL2Selected, fromDateSelected, val, userAccessibleSectorList, 17)
      const requestObject = {
        // fromDate: fromDate?.slice(0, 10) || '',
        // toDate: toDate?.slice(0, 10) || '',
        fromDate:
          fromDateSelected?.length > 0
            ? fromDateSelected[0]?.value
            : `${woodmacDateData.fromDate.slice(0, 4)}-${woodmacDateData.fromDate.slice(4, 6)}-01`,
        toDate: val[0]?.value,
        currentTab:
          Number(localStorage.getItem("ph_woodmac_switch")) === 0
            ? "PET"
            : "rPET"
      };

      setIsWoodmacDataLoading(true);
      dispatch(FetchWoodmacGetReportPageAction(requestObject));
      setPdfUrl(null);
    } 
  };

  /* istanbul ignore next */
  const changeFromDateOptions = toDateMonthSelected => {
    const filteredMonths = changeFromDateOptionsUtil(
      toDateMonthSelected,
      entireMonthList
    );
    setFromDates(filteredMonths);
  };

  /* istanbul ignore next */
  const changeToDateOptions = fromDateMonthSelected => {
    const filteredMonths = changeToDateOptionsUtil(
      fromDateMonthSelected,
      entireMonthList
    );
    setToDates(filteredMonths);
  };

  /* istanbul ignore next */
  const generateMonthObjects = (from, to) => {
    const result = [];

    const start = new Date(
      Number(from.slice(0, 4)),
      Number(from.slice(4, 6)) - 1,
      1
    );
    const end = new Date(Number(to.slice(0, 4)), Number(to.slice(4, 6)) - 1, 1);

    // If start is after end, return empty list
    const monthsDiff =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth());
    if (monthsDiff < 0) return result;

    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      year: "numeric"
    });

    for (let i = 0; i <= monthsDiff; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const year = d.getFullYear();
      const month = d.getMonth() + 1;
      const displayText = formatter.format(d);
      const paddedMonth = String(month).padStart(2, "0");

      result.push({
        id: `${year}-${month}`, // e.g., 2025-3
        displayText, // e.g., Mar 2025
        label: displayText, // e.g., Mar 2025
        value: `${year}-${paddedMonth}-01` // e.g., 2025-03-01
      });
    }

    return result;
  };
  const seriesColors = ["#33ABDA", "#B05788", "#0078A7", "#8458CC", "#FFCC49"];
  // miL2Data's top-level sources/capabilities/name fields are aggregated
  // across ALL L2 subcategories under this L1 category (e.g. Packaging).
  // The values specific to this screen's subcategory (e.g. PET Resin) live
  // in the matching entry of miL2Data.cardDetail, matched by URL slug.
  const effectiveSubcategory = subcategory || "pet-resin";
  const currentCardDetail = miL2Data?.cardDetail?.find(card => card.url?.endsWith(`/${effectiveSubcategory}`));
  const ALL_SOURCES = [
    { displayText: "CMA", id: "CMA", label: "CMA", value: "CMA" },
    // Backend/MI config needs to expose "FASTMARKETS (RISI)" in a category's
    // pipe-delimited sources string (e.g. "SBACCI | CMA | FASTMARKETS (RISI)")
    // for this to appear in the dropdown for that category, same as every
    // other entry in this list.
    { displayText: "FastMarkets (RISI)", id: "FastMarkets (RISI)", label: "FastMarkets (RISI)", value: "FastMarkets (RISI)" },
    { displayText: "S&P Global", id: "S&P Global", label: "S&P Global", value: "S&P Global" },
    { displayText: "SBACCI", id: "SBACCI", label: "SBACCI", value: "SBACCI" },
    { displayText: "SmartCube", id: "SmartCube", label: "SmartCube", value: "SmartCube" },
    { displayText: "Woodmac", id: "Woodmac", label: "Woodmac", value: "Woodmac" }
  ];
  // Only show a source in the dropdown if it's actually available for this
  // L2 category, per currentCardDetail.sources (e.g. "SBACCI | CMA | Woodmac").
  // Falls back to the full list while miL2Data hasn't loaded yet.

  // TODO: uncomment once backend/MI config exposes "SmartCube" in sources.
  // const sourceList = currentCardDetail?.sources
  //   ? ALL_SOURCES.filter(source => currentCardDetail.sources.includes(source.value))
  //   : ALL_SOURCES;
  const sourceList = typeof currentCardDetail?.sources === 'string'
    ? ALL_SOURCES.filter(source => 
        currentCardDetail.sources.split('|').map(s => s.trim().toLowerCase()).includes(source.value.toLowerCase())
      )
    : [];
  // const sourceList = ALL_SOURCES;

  
  // Report counts for the header's "Total number of Market Reports available"
  // line, restricted to only the sources actually present in sourceList.
  const ALL_REPORT_COUNTS = [
    { value: "SBACCI", label: "SBACCI", count: sbacciReportCount },
    { value: "CMA", label: "CMA", count: cmaReportCount },
    { value: "Woodmac", label: "Woodmac", count: woodmacReportCount },
    { value: "S&P Global", label: "S&P Global", count: spGlobalReportCount }
  ];
  const visibleReportCounts = ALL_REPORT_COUNTS.filter(item =>
    sourceList.some(source => source.value === item.value)
  );
  const totalVisibleReportCount = visibleReportCounts.reduce((sum, item) => sum + (item.count || 0), 0);
  
/* istanbul ignore next */
  const onSourceChange = val => {
    if(val && val?.length > 0){
      setSourceSelected(val);
      if (val[0]?.value === "CMA") {
        setIsCmaSelected(true);
        setIsWoodmacSelected(false);
        setIsSBACCISelected(false);
        setIsSPGlobalSelected(false);
        setIsSmartCubeSelected(false);
        setIsRisiSelected(false);
        setSelectedTab(1);
        setCmaPdfReportData({});
        setIsCMADataLoading(true);
        setPdfUrl(null);
      } else if (val[0]?.value === "Woodmac") {
        const defaultValueType = [{ displayText: "Actuals", id: "Actuals", label: "Actuals", value: "actual" }];
        setwoodmacValueTypeSelected(defaultValueType);
        localStorage.setItem("woodmac-valueType", JSON.stringify(defaultValueType));
        setIsCmaSelected(false);
        setIsWoodmacSelected(true);
        setIsSBACCISelected(false);
        setIsSPGlobalSelected(false);
        setIsSmartCubeSelected(false);
        setIsRisiSelected(false);
        setSelectedTab(1);
        setWoodmacReportsSwitcherIndex(0);
        setWoodmacPricingSwitcherIndex(0);
        setCmaPdfReportData({});
        setIsCMADataLoading(true);
        setPdfUrl(null);

      } else if (val[0]?.value === "S&P Global") {
        setIsCmaSelected(false);
        setIsWoodmacSelected(false);
        setIsSBACCISelected(false);
        setIsSPGlobalSelected(true);
        setIsSmartCubeSelected(false);
        setIsRisiSelected(false);
        setSelectedTab(1);
        setPdfUrl(null);
      } else if (val[0]?.value === "SmartCube") {
        setIsCmaSelected(false);
        setIsWoodmacSelected(false);
        setIsSBACCISelected(false);
        setIsSPGlobalSelected(false);
        setIsSmartCubeSelected(true);
        setIsRisiSelected(false);
        setPdfUrl(null);
      } else if (val[0]?.value === "FastMarkets (RISI)") {
        setIsCmaSelected(false);
        setIsWoodmacSelected(false);
        setIsSBACCISelected(false);
        setIsSPGlobalSelected(false);
        setIsSmartCubeSelected(false);
        setIsRisiSelected(true);
        setSelectedTab(1);
        setPdfUrl(null);
      } else {
        setIsCmaSelected(false);
        setIsWoodmacSelected(false);
        setIsSBACCISelected(true);
        setIsSPGlobalSelected(false);
        setIsSmartCubeSelected(false);
        setIsRisiSelected(false);
        setSelectedTab(2);
        setSwitcherIndex(0);
      }
    }
  };

  useEffect(() => {
    if (sourceList.length === 0) return;
    const defaultSource = sourceList.find(source => source.value === "CMA") || sourceList[0];
    setSourceSelected([defaultSource]);
    setIsCmaSelected(defaultSource.value === "CMA");
    setIsWoodmacSelected(defaultSource.value === "Woodmac");
    setIsSBACCISelected(defaultSource.value === "SBACCI");
    setIsSPGlobalSelected(defaultSource.value === "S&P Global");
    setIsSmartCubeSelected(defaultSource.value === "SmartCube");
    setIsRisiSelected(defaultSource.value === "FastMarkets (RISI)");
    setSelectedTab(defaultSource.value === "SBACCI" ? 2 : 1);
  }, [currentCardDetail?.sources]);

  useEffect(() => {
    if(sourceSelected[0]?.displayText == 'SBACCI'){
        dispatch(FetchSBACCIDateAction());
        setSwitcherIndex(0);
        setIsSBACCIDataLoading(true);
    }
  },[sourceSelected])

  function formatCounts(data) {
    // const total = items?.reduce((sum, item) => sum + item.count, 0) || 0;
    // const details = items?.map(item => `${item.name}: ${item.count}`).join(" | ") || "";

    // return `${total} ( ${details} )`;
    const total = data?.reduce((sum, item) => sum + item.count, 0);
    const numStyle = { fontWeight: "700", fontSize: "13px" };

    return (
      <span>
        <span style={numStyle}>{total}</span> ({" "}
        {data?.map((item, index) => (
          <span key={item.name}>
            {item.name}: <span style={numStyle}>{item.count}</span>
            {index < data.length - 1 && " | "}
          </span>
        ))}
        {" "})
      </span>
  );
  }

  /* istanbul ignore next */
  if (authState?.isAuthenticated) {
    return (
      <CmaSourceProvider isUserDataFetched={isUserDataFetched} selectedTab={selectedTab} isCmaSelected={isCmaSelected}>
      <WoodmacSourceProvider isUserDataFetched={isUserDataFetched} selectedTab={selectedTab} isWoodmacSelected={isWoodmacSelected}>
      <SPGlobalSourceProvider isUserDataFetched={isUserDataFetched} isSPGlobalSelected={isSPGlobalSelected} selectedTab={selectedTab} category={category} subcategory={subcategory || "pet-resin"}>
      <SmartCubeSourceProvider isUserDataFetched={isUserDataFetched} isSmartCubeSelected={isSmartCubeSelected} l1Category={miL2Data?.l1Category} l2Category={currentCardDetail?.name}>
      <RisiSourceProvider isUserDataFetched={isUserDataFetched} isRisiSelected={isRisiSelected} l1Category={miL2Data?.l1Category} l2Category={currentCardDetail?.name}>
      <>
        <Breadcrumb
          breadcrumbs={[
            {
              onClick: function aFe() {
                navigate("/overview");
              },
              label: "Home"
            },
            {
              onClick: function aFe() {
                // navigate("/overview/commodity");
                dispatch(setKeyValue({ key: localStoreKeys.commodityL1RestoreSwitcher, value: true }));
                navigate("/overview/mi")
              },
              label: "Market Intelligence Hub"
            },
            {
              onClick: function aFe() {
                navigate(`/overview/mi/${category}`);
              },
              label: miL2Data?.l1Category
            },
            {
              onClick: function aFe() {},
              label: currentCardDetail?.name
            }
          ]}
          homeIcon={{
            icon: "track_changes"
          }}
          style={{ padding: "30px 30px 30px 0px", width: "100%" }}
          className="ph-breadcrumb"
        />
        <div ref={cmaFullPageRef}>

        <Row justify={"start"} style={{ width: "100%" }}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Card style={{ height: "100%", padding: "24px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                  alignItems: "center"
                }}
              >
                <span className="procure-360-header">{currentCardDetail?.name}</span>
                                {/* <span className="powered-by-text" style={{color: "#821D04", marginLeft: '1rem'}}>Powered By SBA CCI & CMA</span> */}
              </div>
              <div style={{width: '100%'}}>
                <div className="risi-title-info" style={{fontSize: "13px", width: '100%'}}>
                  <span style={{fontWeight:"700", fontSize: "13px"}}>SOURCES:</span> {sourceList.map(source => source.displayText).join(" | ")}
                </div>
                <div className="risi-title-info" style={{fontSize: "13px", width: '100%'}}>
                    <span style={{fontWeight:"700", fontSize: "13px"}}>CAPABILITIES:</span> {currentCardDetail?.capabilities}
                </div>
                {/* <div className="risi-title-info" style={{fontSize: "13px", width: '100%', marginTop: "7px"}}>
                    Total number of Market Reports available: <span style={{fontWeight:"700", fontSize: "13px"}}>{totalVisibleReportCount}</span> {visibleReportCounts.length > 1 && (<>({visibleReportCounts.map((item, index) => (
                      <React.Fragment key={item.value}>
                        {item.label}: <span style={{fontWeight:"700", fontSize: "13px"}}>{item.count}</span>{index < visibleReportCounts.length - 1 && " | "}
                      </React.Fragment>
                    ))})</>)}
                </div> */}
                {spGlobalLabelCountData?.l0Category && (
                  <div className="risi-title-info" style={{fontSize: "13px", width: '100%', marginTop: "7px"}}>
                      Total number of price indices / nodes available: {formatCounts(spGlobalLabelCountData?.counts)} 
                  </div>
                )}
                
              </div>
                            {/* <div className="risi-title-info" style={{width: '100%'}}>
                                {userInfo?.FirstName}, The total number of reports for PET and rPET Resin is <span style={{fontWeight: 700}}>{reportData?.reportCount || 0}</span>.
                            </div> */}
                            
                            {/* <div className="modal-update-frequesncy" style={{ marginBottom: "5px", marginTop:"24px", display: "flex", fontSize: "14px",wordWrap:"break-word", whiteSpace: "normal", maxWidth: "858px" }}>{"This application allows access to all data and filters regardless of your role. To see RISI price movement/forecasts, please select the commodity, type of index, and location from the filters below."}</div> */}
                            
                            {/* {(sourceSelected[0]?.displayText == 'SBACCI' ? sbacciLastUpdate : cmaLastUpdate) && <div className="modal-update-frequesncy" style={{ marginBottom:"0px" }}>Data As On: {returnHyphenIfNull(sourceSelected[0]?.displayText == 'SBACCI' ? sbacciLastUpdate : cmaLastUpdate)} | Refresh Frequency: {returnHyphenIfNull(sourceSelected[0]?.displayText == 'SBACCI' ? sbacciRefreshFrequency : cmaRefreshFrequency)}</div>} */}

                            {/* {(reportData?.lastUpdate) && <div className="modal-update-frequesncy">Data As On: {returnHyphenIfNull(reportData?.lastUpdate)} | Refresh Frequency: {returnHyphenIfNull(reportData?.refreshFrequency)}</div>} */}
                           
                            {/* <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                                <div style={{ marginTop: '8px', fontSize: "12px", color: "#2D2D2D" }}>
                                    Explore the reports from the left panel based on the selected date range. You can preview each report and download it if needed.
                                </div>
                            </div> */}
              <hr
                style={{
                  width: "100%",
                  borderTop: "1px solid #DADADA",
                  boxSizing: "border-box"
                }}
              ></hr>

              {/* Source Selection CMA/SBACCI */}
              <div
                style={{
                  width: "100%"
                }}
              >
                <SingleSelectDropdown
                  className="buy-con-dropdown-modal"
                  label="Select Source"
                  childList={sourceList}
                  selectedValue={sourceSelected}
                  setSelectedValue={onSourceChange}
                  // disabled={true}
                />
                {(isSmartCubeSelected || (sourceSelected[0]?.displayText == 'SBACCI' ? sbacciLastUpdate : sourceSelected[0]?.displayText == 'S&P Global' ? spGlobalLastUpdate : sourceSelected[0]?.displayText == 'FastMarkets (RISI)' ? risiLastUpdate : cmaLastUpdate)) &&
                (
                  <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      {
                        !isSmartCubeSelected ? (
                        <div className="modal-update-frequesncy" style={{ marginBottom:"0px", marginTop: '10px' }}>
                          Data As On: {returnHyphenIfNull(sourceSelected[0]?.displayText == 'SBACCI' ? sbacciLastUpdate : sourceSelected[0]?.displayText == 'S&P Global' ? spGlobalLastUpdate : sourceSelected[0]?.displayText == 'FastMarkets (RISI)' ? risiLastUpdate : cmaLastUpdate)} | Refresh Frequency: {returnHyphenIfNull(sourceSelected[0]?.displayText == 'SBACCI' ? sbacciRefreshFrequency : sourceSelected[0]?.displayText == 'S&P Global' ? spGlobalRefreshFrequency : sourceSelected[0]?.displayText == 'FastMarkets (RISI)' ? risiRefreshFrequency : cmaRefreshFrequency)}
                        </div>
                        ):(
                          <div className="modal-update-frequesncy" style={{ marginBottom:"0px", marginTop: '10px' }}>
                            Refresh Frequency: {returnHyphenIfNull(smartCubeRefreshFrequency)}
                          </div>
                        )
                      }
                      {
                        (isCmaSelected || isSBACCISelected || isWoodmacSelected || isSPGlobalSelected || isSmartCubeSelected || isRisiSelected) &&
                        (
                            <div>
                              <PricingPageDownloadControls
                                isCmaSelected={isCmaSelected}
                                isWoodmacSelected={isWoodmacSelected}
                                isSBACCISelected={isSBACCISelected}
                                isSPGlobalSelected={isSPGlobalSelected}
                                selectedTab={selectedTab}
                                cmaFullPageRef={cmaFullPageRef}
                                isSmartCubeSelected={isSmartCubeSelected}
                                isRisiSelected={isRisiSelected}
                              />
                            </div>
                        )
                      }
                  </div>
                    
                  </>
                )
                }
                
              </div>

              

              {/* Pricing And Reports Navigation - not shown for SmartCube or RISI */}
              {!isSmartCubeSelected && !isRisiSelected && (
                <div style={{ width: "100%" }}>
                  <TabsNavigation
                    className="resins-analytics-tabs"
                    selectedIndex={selectedTab - 1}
                    style={{ width: "100%" }}
                  >
                    <Tab
                      label="Pricing"
                      style={{ width: "50%" }}
                      onClick={function Xs() {
                        setSelectedTab(1);
                      }}
                      disabled={isSBACCISelected}
                    />
                    <Tab
                      label="Reports"
                      style={{ width: "50%" }}
                      onClick={function Xs() {
                        setSelectedTab(2);
                      }}
                    />
                  </TabsNavigation>
                </div>
              )}

              {/* Pricing Tab Dropdown Options in CMA */}
              {isCmaSelected && (
                <CmaSource section="filters" />
              )}

              {/* Pricing Tab Dropdown Options in Woodmac */}
              {isWoodmacSelected && (
                <WoodmacSource section="filters" />
              )}

              {/* Pricing/Reports Tab Dropdown Options in S&P Global */}
              {isSPGlobalSelected && (
                <SPSource section="filters" />
              )}

              {/* Filter Dropdown Options in SmartCube */}
              {isSmartCubeSelected && (
                <SmartCubeSource section="filters" />
              )}

              {/* Filter Dropdown Options in RISI (FASTMARKETS) */}
              {isRisiSelected && (
                <RisiSource section="filters" />
              )}

              {/* Reports Tab Dropdown Options in SBACCI */}
              {(isSBACCISelected || isWoodmacSelected) && selectedTab === 2 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    width: "100%"
                  }}
                >
                  <Row
                    gutter={[10, 10]}
                    justify="start"
                    style={{ width: "100%", marginTop: "12px" }}
                  >
                    <Col xs={24} sm={24} md={3} lg={3} xl={4}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          width: "100%",
                          flexDirection: "column"
                        }}
                      >
                        <div
                          style={{
                            fontFamily: "Inter",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            marginBottom: "3px"
                          }}
                        >
                          Category
                        </div>
                        <Switcher
                          className=""
                          items={[
                            {
                              label: "PET",
                              leadingIcon: "edit",
                              showLeadingIcon: false,
                              style: {
                                width: "50%",
                                display: "flex",
                                justifyContent: "center"
                              }
                            },
                            {
                              label: "rPET",
                              leadingIcon: "edit",
                              showLeadingIcon: false,
                              style: {
                                width: "50%",
                                display: "flex",
                                justifyContent: "center"
                              }
                            }
                          ]}
                          selectedItemIndex={isSBACCISelected ? switcherIndex : isWoodmacSelected ? woodmacReportsSwitcherIndex : 0}
                          onUpdate={val => {
                            switchTabs(val);
                          }}
                          style={{
                            display: "flex",
                            justifyContent: "flex-start",
                            width: "100%"
                          }}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={24} md={5} lg={5} xl={5}>
                      
                      <SingleSelectDropdown
                        className="buy-con-dropdown-modal"
                        label="From Date"
                        childList={fromDates}
                        selectedValue={fromDateSelected}
                        setSelectedValue={isSBACCISelected ? onFromDateChange : isWoodmacSelected ? onWoodmacFromDateChange : () => {} }
                        // disabled={capability===appNames.forestreet && appName==="Veridion (V)" && selectedTab==2}
                      />
                    </Col>
                    <Col xs={24} sm={24} md={5} lg={5} xl={5}>
                      <SingleSelectDropdown
                        className="buy-con-dropdown-modal"
                        label="To Date"
                        childList={toDates}
                        selectedValue={toDateSelected}
                        setSelectedValue={isSBACCISelected ? onToDateChange : isWoodmacSelected ? onWoodmacToDateChange : () => {} }
                        // disabled={capability===appNames.forestreet && appName==="Veridion (V)" && selectedTab==2}
                      />
                    </Col>
                  </Row>
                </div>
              )}

            </Card>
          </Col>
        </Row>


        {/* Reports Content in CMA */}
        {isCmaSelected && (
          <CmaSource section="content" />
        )}

        {/* Reports Content in Woodmac */}
        {isWoodmacSelected && (
          <WoodmacSource
            section="content"
            isWoodmacDataLoading={isWoodmacDataLoading}
            renderedWoodmacSections={renderedWoodmacSections}
            woodmacAccordionSelectedIndex={woodmacAccordionSelectedIndex}
            cmaPdfReportData={cmaPdfReportData}
            renderSection={renderSection}
            pdfUrl={pdfUrl}
          />
        )}

        {/* Reports Content in SBACCI */}
        {isSBACCISelected && (
          <SbacciSource
            selectedTab={selectedTab}
            isSBACCIDataLoading={isSBACCIDataLoading}
            reportData={reportData}
            renderedSections={renderedSections}
            accordionSelectedIndex={accordionSelectedIndex}
            renderSection={renderSection}
            pdfUrl={pdfUrl}
          />
        )}

        {/* Pricing/Reports Content in S&P Global */}
        {isSPGlobalSelected && (
          <SPSource section="content" />
        )}

        {/* Sample Content in SmartCube */}
        {isSmartCubeSelected && (
          <SmartCubeSource section="content" />
        )}

        {/* Content in RISI (FASTMARKETS) */}
        {isRisiSelected && (
          <RisiSource section="content" />
        )}

        <br />
        <div className="spend-modal-manage-spend" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: '100%' }}>
            <div style={{width: '100%'}}>Please reach out to {(sourceSelected[0]?.displayText == 'SBACCI' ? sbacciAppOwners : sourceSelected[0]?.displayText == 'S&P Global' ? spGlobalAppOwners : sourceSelected[0]?.displayText == 'SmartCube' ? smartCubeAppOwners : sourceSelected[0]?.displayText == 'FastMarkets (RISI)' ? risiAppOwners : cmaAppOwners)?.map((contact, index) => (
              <React.Fragment key={index}>
                <a href={`mailto:${contact.emailId}`} style={{ color: "#005cbc", textDecoration: "None" }}>{contact.emailId}</a>
                  {
                    sourceSelected[0]?.displayText == 'SBACCI' ? (
                      <>
                        {index < sbacciAppOwners?.length - 1 && contact.emailId.includes('ates') && ' for PET , '}
                        {index < sbacciAppOwners?.length - 1 && contact.emailId.includes('maddox') && ' for rPET or '}
                      </>
                    ) : sourceSelected[0]?.displayText == 'S&P Global' ? (
                      <>
                        {index < spGlobalAppOwners?.length - 1 && ', '}
                      </>
                    ) : sourceSelected[0]?.displayText == 'SmartCube' ? (
                      <>
                        {index < smartCubeAppOwners?.length - 1 && ', '}
                      </>
                    ) : sourceSelected[0]?.displayText == 'FastMarkets (RISI)' ? (
                      <>
                        {index < risiAppOwners?.length - 1 && ', '}
                      </>
                    ) : (
                      <>
                        {index < cmaAppOwners?.length - 1 && ', '}
                      </>
                    )
                  }

                {/* {index < (sourceSelected[0]?.displayText == 'SBACCI' ? sbacciAppOwners : cmaAppOwners)?.length - 1 && ', '} */}
              </React.Fragment>
            ))} for any query{!isSBACCISelected ? ' on the data displayed' : ''}.</div>
            <div style={{width: '100%'}}>Please fill in <a href={feedbackFormUrl} target="_blank" style={{ color: "#005cbc", textDecoration: "None" }}>this form</a> for any feedback related to Procurement Hub.</div>
        </div>
        <br/>
        </div>
      </>
      </RisiSourceProvider>
      </SmartCubeSourceProvider>
      </SPGlobalSourceProvider>
      </WoodmacSourceProvider>
      </CmaSourceProvider>
    );
  }
};

export default SBACCIScreen;